import styles from './enhance-with-click-outside.module.css'

import hoistNonReactStatics from 'hoist-non-react-statics'
import * as React from 'react'

type WrappedInstance = { handleClickOutside?: (e: MouseEvent) => void }

// Replacement for react-click-outside, which relies on the removed findDOMNode.
export default function enhanceWithClickOutside(
  WrappedComponent: React.ComponentType<any>,
) {
  const componentName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  const Wrapped = WrappedComponent as any

  class EnhancedComponent extends React.Component<any> {
    private containerRef = React.createRef<HTMLDivElement>()
    private wrappedInstance: WrappedInstance | null = null

    componentDidMount() {
      document.addEventListener('click', this.handleClickOutside, true)
    }

    componentWillUnmount() {
      document.removeEventListener('click', this.handleClickOutside, true)
    }

    handleClickOutside = (e: MouseEvent) => {
      const node = this.containerRef.current
      if (
        (!node || !node.contains(e.target as Node)) &&
        typeof this.wrappedInstance?.handleClickOutside === 'function'
      ) {
        this.wrappedInstance.handleClickOutside(e)
      }
    }

    render() {
      const { wrappedRef, ...rest } = this.props
      return (
        <div ref={this.containerRef} className={styles.contents}>
          <Wrapped
            {...rest}
            ref={(c: WrappedInstance | null) => {
              this.wrappedInstance = c
              if (wrappedRef) {
                wrappedRef(c)
              }
            }}
          />
        </div>
      )
    }
  }

  ;(
    EnhancedComponent as React.ComponentType
  ).displayName = `clickOutside(${componentName})`

  return hoistNonReactStatics(EnhancedComponent, WrappedComponent as any)
}
